'use client'

/**
 * Modal-popover trigger for the `form_embed` "popover" layout. A button
 * that opens a centered modal containing the form. Click-outside or Esc
 * closes; submit success leaves the modal open with the success message
 * (`FormRender` handles that internally).
 *
 * Dialog chrome (role=dialog/aria-modal, Esc, focus trap, backdrop dismiss,
 * scroll-lock, close button) is owned by the shared ModalShell.
 *
 * Kept in storefront/src so it can be a client component while the server
 * component `FormSection` resolves the form definition.
 */

import { useState, type ReactNode } from 'react'
import { ModalShell } from '@sifly/shared-ui/overlay'

interface FormPopoverTriggerProps {
  triggerLabel: string
  title?: string
  description?: string
  children: ReactNode
}

export function FormPopoverTrigger({
  triggerLabel,
  title,
  description,
  children,
}: FormPopoverTriggerProps) {
  const [open, setOpen] = useState(false)

  return (
    <section className="mx-auto max-w-[var(--section-max-width,42rem)] px-[var(--section-padding-x,1rem)] py-8 [text-align:var(--section-text-align,center)]">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md bg-[var(--color-cta,#000)] px-6 py-3 text-sm font-semibold text-[var(--color-cta-text,#fff)] hover:opacity-90"
      >
        {triggerLabel}
      </button>
      <ModalShell
        open={open}
        onClose={() => setOpen(false)}
        placement="center"
        size="lg"
        showClose
        label={title ?? triggerLabel}
      >
        {title && (
          <h2
            className="mb-1 text-left text-xl font-semibold"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {title}
          </h2>
        )}
        {description && (
          <p className="mb-4 text-left text-sm text-[var(--color-text-secondary)]">{description}</p>
        )}
        <div className="text-left">{children}</div>
      </ModalShell>
    </section>
  )
}
